import { Card, Table, Button, Space, Typography, InputNumber, message, Tag, Row, Col, Statistic, Popconfirm, Select, Tooltip, Alert } from 'antd';
import { ThunderboltOutlined, PlusOutlined, ReloadOutlined, SwapRightOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../lib/api-client';

const { Text } = Typography;

interface CityRef {
  id: number;
  nameUz: string;
  nameRu: string;
}

interface RegionRef {
  id: number;
  nameUz: string;
  nameRu: string;
}

interface DerivedRow {
  fromRegionId: number;
  toRegionId: number;
  fromCityId: number | null;
  toCityId: number | null;
  fromDistrictId: number | null;
  toDistrictId: number | null;
  fromKind: 'city' | 'district';
  toKind: 'city' | 'district';
  fromCity: CityRef | null;
  toCity: CityRef | null;
  fromDistrict: CityRef | null;
  toDistrict: CityRef | null;
  fromRegion: RegionRef;
  toRegion: RegionRef;
  tripType: string;
  sourceRouteId: number;
  sourceFromName: string;
  sourceToName: string;
  distanceKm: number;
  baseDistanceKm: number;
  deltaKm: number;
  deltaPriceRaw: number;
  deltaPrice: number;
  basePrice: number;
  pricePerKm: number;
  minPrice: number;
  maxPrice: number | null;
  economPrice: number | null;
  standardPrice: number | null;
  comfortPrice: number | null;
  businessPrice: number | null;
  sevenPlusPrice: number | null;
  surgeMultiplier: number;
}

interface DeriveResponse {
  ratePerKm: number;
  derived: DerivedRow[];
  stats: {
    totalDerived: number;
    cityDerived: number;
    districtDerived: number;
    skippedExisting: number;
    skippedMissingCoords: number;
    sourceRoutes: number;
  };
}

const endpointName = (kind: 'city' | 'district', city: CityRef | null, district: CityRef | null) =>
  kind === 'district' ? district?.nameUz ?? '?' : city?.nameUz ?? '?';

const tripTypeLabels: Record<string, string> = {
  INTERREGION_TAXI: 'Viloyatlar aro',
  SHAHARLAR_ARO: 'Viloyat ichida',
  YOL_YOLAKAY: 'Savdolashish',
};
const tripTypeColors: Record<string, string> = {
  INTERREGION_TAXI: 'blue',
  SHAHARLAR_ARO: 'cyan',
  YOL_YOLAKAY: 'green',
};

const formatShort = (v: number | null | undefined) => {
  if (v == null) return '—';
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1000) return `${Math.round(v / 1000)}k`;
  return `${v}`;
};

const formatSom = (v: number | null) => {
  if (v == null) return '—';
  return new Intl.NumberFormat('uz-UZ').format(v);
};

const currencyFormatter = (value: any) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
const currencyParser = (value: any) => value!.replace(/\s?/g, '');

export const DerivedPricingTest = () => {
  const [rate, setRate] = useState<number>(500);
  const [appliedRate, setAppliedRate] = useState<number>(500);
  const [data, setData] = useState<DeriveResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [fromRegionFilter, setFromRegionFilter] = useState<number | undefined>();
  const [toRegionFilter, setToRegionFilter] = useState<number | undefined>();
  const [tripTypeFilter, setTripTypeFilter] = useState<string | undefined>();
  const [kindFilter, setKindFilter] = useState<'all' | 'city' | 'district'>('all');

  const fetchDerived = async (r: number) => {
    setLoading(true);
    try {
      const response = await apiClient.get('/admin/pricing/routes/derive', { params: { rate: r } });
      setData(response.data);
      setAppliedRate(r);
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Hisoblashda xatolik');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDerived(rate);
  }, []);

  const handleApply = async () => {
    setApplying(true);
    try {
      const response = await apiClient.post('/admin/pricing/routes/derive/apply', { rate: appliedRate });
      const inserted = response.data?.inserted ?? 0;
      message.success(`${inserted} ta narx bazaga qo'shildi`);
      // Refresh — derived list will now be smaller (or empty) since they exist
      await fetchDerived(appliedRate);
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Qo\'shishda xatolik');
    } finally {
      setApplying(false);
    }
  };

  const fromRegions = useMemo(() => {
    if (!data) return [];
    const seen = new Map<number, RegionRef>();
    for (const d of data.derived) seen.set(d.fromRegionId, d.fromRegion);
    return Array.from(seen.values()).sort((a, b) => a.nameUz.localeCompare(b.nameUz));
  }, [data]);

  const toRegions = useMemo(() => {
    if (!data) return [];
    const seen = new Map<number, RegionRef>();
    for (const d of data.derived) seen.set(d.toRegionId, d.toRegion);
    return Array.from(seen.values()).sort((a, b) => a.nameUz.localeCompare(b.nameUz));
  }, [data]);

  const tripTypes = useMemo(() => {
    if (!data) return [];
    return Array.from(new Set(data.derived.map((d) => d.tripType)));
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.derived.filter((d) => {
      if (fromRegionFilter && d.fromRegionId !== fromRegionFilter) return false;
      if (toRegionFilter && d.toRegionId !== toRegionFilter) return false;
      if (tripTypeFilter && d.tripType !== tripTypeFilter) return false;
      if (kindFilter === 'city' && (d.fromKind !== 'city' || d.toKind !== 'city')) return false;
      if (kindFilter === 'district' && d.fromKind !== 'district' && d.toKind !== 'district') return false;
      return true;
    });
  }, [data, fromRegionFilter, toRegionFilter, tripTypeFilter, kindFilter]);

  const columns = [
    {
      title: 'Yo\'nalish',
      key: 'route',
      width: 360,
      render: (_: unknown, row: DerivedRow) => (
        <Space direction="vertical" size={0}>
          <Space size={4}>
            <Text strong>{endpointName(row.fromKind, row.fromCity, row.fromDistrict)}</Text>
            {row.fromKind === 'district' && <Tag color="geekblue">tuman</Tag>}
            <SwapRightOutlined style={{ color: '#1ABC9C' }} />
            <Text strong>{endpointName(row.toKind, row.toCity, row.toDistrict)}</Text>
            {row.toKind === 'district' && <Tag color="geekblue">tuman</Tag>}
          </Space>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {row.fromRegion?.nameUz} → {row.toRegion?.nameUz}
          </Text>
        </Space>
      ),
      sorter: (a: DerivedRow, b: DerivedRow) =>
        ((a.fromRegion?.nameUz || '') + endpointName(a.fromKind, a.fromCity, a.fromDistrict)).localeCompare(
          (b.fromRegion?.nameUz || '') + endpointName(b.fromKind, b.fromCity, b.fromDistrict),
        ),
    },
    {
      title: 'Tur',
      dataIndex: 'tripType',
      key: 'tripType',
      width: 110,
      render: (t: string) => <Tag color={tripTypeColors[t] || 'blue'}>{tripTypeLabels[t] || t}</Tag>,
    },
    {
      title: () => (
        <Tooltip title="Asos qilingan yo'nalish: ushbu narx unga nisbatan hisoblangan">
          <span>Asosi <InfoCircleOutlined style={{ color: '#7F8C8D' }} /></span>
        </Tooltip>
      ),
      key: 'source',
      width: 200,
      render: (_: unknown, row: DerivedRow) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 12 }}>
            {row.sourceFromName} → {row.sourceToName}
          </Text>
          <Text type="secondary" style={{ fontSize: 11 }}>{row.baseDistanceKm} km</Text>
        </Space>
      ),
    },
    {
      title: 'Masofa',
      key: 'distance',
      width: 110,
      render: (_: unknown, row: DerivedRow) => (
        <Space direction="vertical" size={0}>
          <Text>{row.distanceKm} km</Text>
          <Text
            style={{ fontSize: 11, color: row.deltaKm > 0 ? '#cf1322' : row.deltaKm < 0 ? '#389e0d' : '#7F8C8D' }}
          >
            {row.deltaKm > 0 ? '+' : ''}{row.deltaKm} km
          </Text>
        </Space>
      ),
      sorter: (a: DerivedRow, b: DerivedRow) => a.deltaKm - b.deltaKm,
    },
    {
      title: 'Narx farqi',
      dataIndex: 'deltaPrice',
      key: 'deltaPrice',
      width: 120,
      render: (v: number, row: DerivedRow) => (
        <Tooltip title={`Aniq farqi: ${row.deltaPriceRaw > 0 ? '+' : ''}${formatSom(row.deltaPriceRaw)} so'm (10k gacha pastga yumaltirilgan)`}>
          <Tag color={v > 0 ? 'red' : v < 0 ? 'green' : 'default'}>
            {v > 0 ? '+' : ''}{formatShort(v)}
          </Tag>
        </Tooltip>
      ),
      sorter: (a: DerivedRow, b: DerivedRow) => a.deltaPrice - b.deltaPrice,
    },
    {
      title: 'Tarif narxlari',
      key: 'prices',
      render: (_: unknown, row: DerivedRow) => {
        if (row.tripType === 'INTERREGION_TAXI') {
          return (
            <Space size={4} wrap>
              {row.economPrice != null && <Tag>E: {formatShort(row.economPrice)}</Tag>}
              {row.standardPrice != null && <Tag color="blue">S: {formatShort(row.standardPrice)}</Tag>}
              {row.comfortPrice != null && <Tag color="cyan">K: {formatShort(row.comfortPrice)}</Tag>}
              {row.businessPrice != null && <Tag color="purple">B: {formatShort(row.businessPrice)}</Tag>}
              {row.sevenPlusPrice != null && <Tag color="orange">7+: {formatShort(row.sevenPlusPrice)}</Tag>}
            </Space>
          );
        }
        return (
          <Text>
            {formatShort(row.minPrice)} — {row.maxPrice != null ? formatShort(row.maxPrice) : '∞'}
          </Text>
        );
      },
    },
  ];

  const totalDerived = data?.stats.totalDerived ?? 0;
  const filteredCount = filtered.length;

  return (
    <Card>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Yaqin shahar va tumanlar uchun narxlarni avtomatik hisoblash"
        description={
          <span>
            Mavjud anchor (shahar-shahar) yo'nalishlardan har bir viloyatdagi boshqa shahar va tumanlar uchun narxlar hisoblanadi.
            Algoritm: <Text code>delta_km × rate</Text>, eng yaqin 10 000 ga pastga yumaltiriladi.
            Masofa Haversine usulida hisoblanadi (kompas yo'nalishini hisobga oladi).
            <br />
            <Text type="secondary">
              Yumshoq tahmin: joy yo'lda bo'lsa narx pasayadi, qarama-qarshi tomonda bo'lsa oshadi.
              Tuman-darajadagi narxlar <Text code>from_district_id</Text>/<Text code>to_district_id</Text> ustunlariga yoziladi.
            </Text>
          </span>
        }
      />

      <Row gutter={16} align="middle" style={{ marginBottom: 16 }}>
        <Col xs={24} md={6}>
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Har km uchun qo'shimcha (so'm)</Text>
            <InputNumber
              style={{ width: '100%' }}
              value={rate}
              onChange={(v) => setRate(Number(v) || 0)}
              min={0}
              max={5000}
              step={50}
              formatter={currencyFormatter}
              parser={currencyParser}
            />
          </Space>
        </Col>
        <Col xs={24} md={4}>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => fetchDerived(rate)}
            loading={loading}
            block
            style={{ marginTop: 18 }}
          >
            Hisoblash
          </Button>
        </Col>
        <Col xs={12} md={3}>
          <Statistic
            title="Yaratiladigan"
            value={totalDerived}
            valueStyle={{ color: '#1ABC9C', fontSize: 20 }}
          />
        </Col>
        <Col xs={12} md={3}>
          <Statistic
            title="Shahar"
            value={data?.stats.cityDerived ?? 0}
            valueStyle={{ color: '#1890FF', fontSize: 20 }}
          />
        </Col>
        <Col xs={12} md={3}>
          <Statistic
            title="Tuman"
            value={data?.stats.districtDerived ?? 0}
            valueStyle={{ color: '#722ED1', fontSize: 20 }}
          />
        </Col>
        <Col xs={12} md={3}>
          <Statistic
            title="Mavjud"
            value={data?.stats.skippedExisting ?? 0}
            valueStyle={{ color: '#7F8C8D', fontSize: 20 }}
          />
        </Col>
        <Col xs={24} md={4}>
          <Statistic
            title="Asos yo'nalishlar"
            value={data?.stats.sourceRoutes ?? 0}
            prefix={<ThunderboltOutlined style={{ color: '#FAAD14' }} />}
            valueStyle={{ fontSize: 20 }}
          />
        </Col>
      </Row>

      <Row gutter={8} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={6}>
          <Select
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Qayerdan (viloyat)"
            value={fromRegionFilter}
            onChange={setFromRegionFilter}
            options={fromRegions.map((r) => ({ value: r.id, label: r.nameUz }))}
          />
        </Col>
        <Col xs={24} sm={6}>
          <Select
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Qayerga (viloyat)"
            value={toRegionFilter}
            onChange={setToRegionFilter}
            options={toRegions.map((r) => ({ value: r.id, label: r.nameUz }))}
          />
        </Col>
        <Col xs={24} sm={6}>
          <Select
            style={{ width: '100%' }}
            allowClear
            placeholder="Sayohat turi"
            value={tripTypeFilter}
            onChange={setTripTypeFilter}
            options={tripTypes.map((t) => ({ value: t, label: tripTypeLabels[t] || t }))}
          />
        </Col>
        <Col xs={24} sm={6}>
          <Select
            style={{ width: '100%' }}
            value={kindFilter}
            onChange={setKindFilter}
            options={[
              { value: 'all', label: 'Barchasi (shahar + tuman)' },
              { value: 'city', label: 'Faqat shaharlar' },
              { value: 'district', label: 'Faqat tumanlar' },
            ]}
          />
        </Col>
      </Row>

      {filteredCount !== totalDerived && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Filtrlangan: {filteredCount} / {totalDerived}
        </Text>
      )}

      <Table
        dataSource={filtered}
        columns={columns}
        rowKey={(row) => `${row.fromRegionId}-${row.toRegionId}-${row.fromCityId ?? 'n'}-${row.toCityId ?? 'n'}-${row.fromDistrictId ?? 'n'}-${row.toDistrictId ?? 'n'}-${row.tripType}`}
        loading={loading}
        size="small"
        pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: [25, 50, 100, 200] }}
      />

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
        <Popconfirm
          title={`${totalDerived} ta narxni bazaga qo'shasizmi?`}
          description={`Joriy stavka: ${currencyFormatter(appliedRate)} so'm/km. Mavjud yo'nalishlar avtomatik o'tkazib yuboriladi.`}
          onConfirm={handleApply}
          okText="Ha, qo'shish"
          cancelText="Bekor qilish"
          disabled={totalDerived === 0}
        >
          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            loading={applying}
            disabled={totalDerived === 0}
          >
            Qo'shish ({totalDerived})
          </Button>
        </Popconfirm>
      </div>
    </Card>
  );
};
